import { useEffect, useCallback, useRef } from 'react'
import { useCircle360AccountMismatchModal } from '@/features/account/components/Membership/hooks'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import {
  useIsMember,
  useIsUserC360,
  useUser,
  useUserDefaultShoppingAddressId,
} from '@/features/account/services/User/hooks'
import { type GHPProps } from '@/features/shop/components/GlobalHomePage'
import { useGetImperativeIsDialogOpened } from '@/features/shared/state/Dialog/hooks'
import { useQueryMembershipPlan } from '@/features/account/services/MembershipPlan/queries'
import { trackElementViewed } from '@/features/shared/analytics/element'
import { DynamicConfirmationModal } from '@/features/shared/components/Modal/ConfirmationModal/DynamicConfirmationModal'
import { DynamicSnapEBTMembershipEntryModal } from '@/features/purchase/components/Modal/SnapEBTMembershipEntryModal/DynamicSnapEBTMembershipEntryModal'
import { useCanShowViewType } from '@/features/shared/services/ViewedElementTracker/hooks'
import { trackViewedTracker } from '@/features/shared/services/ViewedElementTracker/utils'
import { DynamicEvergreenNmuModal } from '@/features/shared/components/Modal/EvergreenNmuModal/DynamicEvergreenNmuModal'
import { useCanDisplayCMSModal } from '@/features/shared/hooks/useCanDisplayCMSModal'
import { useIsEligibleForC360NoMarkup } from '@/features/entrypoint/components/Circle360/useIsEligibleForC360NoMarkup'
import { consumeBusinessProfileCreated } from '@/features/account/services/Business/utils'

/**
 * Only "operational" dialogs are allowed in this component.
 * Operational dialogs keep our business running and prevent incidents.
 * If hiding your dialog means we actively lose money or receive member
 * complaints, it is operational. Otherwise, it should be added from the
 * CMS.
 */
export const GlobalHomeModals = ({ c360MembershipId, checkC360Membership }: GHPProps) => {
  // ************************************************************************* //
  // --- modal hooks ----
  const { openDialog } = useHandleDialog()
  const getIsDialogOpened = useGetImperativeIsDialogOpened()
  const user = useUser()
  const is360User = useIsUserC360()
  const circle360AccountMismatchModalProps = useCircle360AccountMismatchModal()
  // prevents consecutive GHP modals from displaying
  const hasShownModalRef = useRef(false)
  // modals tracked in viewed element service
  const [canShowSnapEbtEntryModal, setHasSeenSnapEbtModal] = useCanShowViewType('SnapEbtEntryModal')
  const [canShowCircle360NMUModal, setHasSeenCircle360NMUModal] = useCanShowViewType(
    'Circle360NMUEvergreenModal',
  )
  const canDisplayCMSModal = useCanDisplayCMSModal(!!checkC360Membership)
  // ************************************************************************* //

  const { isFetched: isMembershipStatusFetched } = useQueryMembershipPlan()

  const isMember = useIsMember()
  const userDefaultShoppingAddressId = useUserDefaultShoppingAddressId()
  const isEligibleForC360NoMarkup = useIsEligibleForC360NoMarkup()

  // ensure we have a user before applying mismatch logic
  const circle360AccountMismatch =
    user &&
    // logged in user is not c360 user
    ((checkC360Membership && !is360User) ||
      // logged in user is c360 user but membership ids dont match
      (c360MembershipId && c360MembershipId !== user.platformCustomerId))

  // ************************************************************************* //
  // experiments and feature flags
  // ************************************************************************* //

  // snap ebt modal logic
  const shouldShowSnapEBTEntryModal =
    isMembershipStatusFetched &&
    !isMember && // isPPO
    canShowSnapEbtEntryModal &&
    !!user?.hasEbtPaymentMethod &&
    userDefaultShoppingAddressId
  /*
    Logic for opening membership GHP entry modals
  */
  const triggerMembershipModals = useCallback(() => {
    if (shouldShowSnapEBTEntryModal) {
      trackViewedTracker({
        canShowPopupModal: canShowSnapEbtEntryModal,
        elementName: 'SnapEBTMembershipEntryModal',
      })
      openDialog(DynamicSnapEBTMembershipEntryModal)
      setHasSeenSnapEbtModal()
      hasShownModalRef.current = true
    }
  }, [shouldShowSnapEBTEntryModal, canShowSnapEbtEntryModal, setHasSeenSnapEbtModal, openDialog])

  // ******
  // effect to trigger non-guest modals
  useEffect(() => {
    // only show GHP modals if no existing modals are displayed
    if (getIsDialogOpened()) return
    if (circle360AccountMismatch && !hasShownModalRef.current) {
      // prevent consecutive modals
      hasShownModalRef.current = true
      openDialog(DynamicConfirmationModal, circle360AccountMismatchModalProps)
      trackElementViewed({
        type: 'modal',
        message_goal: 'circle360_u8_signed_into_a_different_account',
      })
    }

    // only show consecutive modals if they are highest priority
    if (hasShownModalRef.current) return

    if (consumeBusinessProfileCreated()) {
      hasShownModalRef.current = true
      openDialog(DynamicConfirmationModal, {
        size: 'md',
        contentLabel: 'Business profile created',
        modalTitle: "You're in business!",
        modalMessage: 'Switch between your personal and business profiles any time from the menu.',
        acceptButtonLabel: 'Got it',
        showReject: false,
      })
      return
    }

    if (!canDisplayCMSModal) return

    // show membership modals, if applicable
    triggerMembershipModals()

    //show C360 evergreen nmu modal
    const showC360NMUEvergreenModal = canShowCircle360NMUModal && isEligibleForC360NoMarkup
    if (showC360NMUEvergreenModal) {
      openDialog(DynamicEvergreenNmuModal)
      setHasSeenCircle360NMUModal()
      hasShownModalRef.current = true
    }
  }, [
    canDisplayCMSModal,
    triggerMembershipModals,
    getIsDialogOpened,
    circle360AccountMismatch,
    circle360AccountMismatchModalProps,
    openDialog,
    canShowCircle360NMUModal,
    setHasSeenCircle360NMUModal,
    isEligibleForC360NoMarkup,
  ])
  return null
}
